import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { DateTime } from "luxon";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TimezoneService } from "@/lib/timezone";

interface ConnectionStatusWidgetProps {
  health: {
    hasConnection: boolean;
    authMode?: "OWNER_OAUTH" | "MANAGED_PLATFORM" | null;
    managerEmail?: string | null;
    lastSyncAt?: string | null;
    lastSyncError?: string | null;
    nextScheduledSyncAt?: string | null;
    syncEnabled: boolean;
    reauthRequired: boolean;
    managerReauthRequired: boolean;
    managerActive: boolean;
  };
  onReconnect: () => void;
  reconnecting: boolean;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "Never";
  return DateTime.fromISO(iso, { zone: "utc" })
    .setZone(TimezoneService.getCurrentSalonTimezone())
    .toFormat("MMM dd, yyyy • h:mm a");
}

export function ConnectionStatusWidget({
  health,
  onReconnect,
  reconnecting,
}: ConnectionStatusWidgetProps) {
  if (!health.hasConnection) return null;

  const isManaged = health.authMode === "MANAGED_PLATFORM";
  // Order matters: deactivation is the highest-priority CTA because it blocks
  // scheduled sync and is only fixable by platform admin.
  const managerInactive = isManaged && !health.managerActive;
  const reauthCta = health.reauthRequired ? (isManaged ? "managed" : "owner") : null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {managerInactive || health.reauthRequired ? (
            <AlertTriangle className="h-4 w-4 text-destructive" />
          ) : (
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          )}
          Sync status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">Last sync</div>
            <div>{formatDate(health.lastSyncAt)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Next scheduled
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-muted-foreground" />
              {formatDate(health.nextScheduledSyncAt)}
            </div>
          </div>
        </div>

        {health.lastSyncError && !health.reauthRequired && !managerInactive ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">{health.lastSyncError}</AlertDescription>
          </Alert>
        ) : null}

        {managerInactive ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Sync is paused — our manager account has been deactivated. Contact 96digital support
              to restore syncing.
            </AlertDescription>
          </Alert>
        ) : null}

        {reauthCta === "owner" ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="space-y-2">
              <div>Google revoked our access. Reconnect to resume sync.</div>
              <Button size="sm" onClick={onReconnect} disabled={reconnecting}>
                {reconnecting ? "Starting..." : "Reconnect Google"}
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {reauthCta === "managed" ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Sync is paused because our manager account needs re-authentication. Contact 96digital
              support — we'll fix it on our end.
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}
